// Paste lines into a PO (M3): rows copied from a spreadsheet or a vendor's PI (proforma invoice), read into PO lines
// for the PO's vendor. Each row's item is found by HHL's SKU, this vendor's SKU (its vendor_item) or the barcode; its
// quantity, unit price and promised date come from the columns the header names (SKU, Qty, Price, Date…) or, without
// a header, from what the columns hold (the column of items, a qty × price = amount trio, dates). People can change
// what a column is in the preview (web/src/pages/po/PasteLines.tsx), and every row says what's doubtful: these lines go to vendors,
// so a guess never goes in quietly. Then every line goes in at once through po.add_lines. Shared so the browser, the
// server and tests read a paste the same way.
import type { Item, VendorItem } from "./db.gen.ts";
import { hasSkuPrefix, skuStem } from "./part-sku.ts";
import { type QtyWarning, qtyWarnings, type VendorTerms } from "./po.ts";

export type PasteItem = Pick<Item, "id" | "sku" | "name" | "barcode" | "archived" | "photo_url">;
export type PasteLink = Pick<VendorItem, "item_id"> & VendorTerms;

export const COLUMN_KINDS = ["id", "qty", "price", "amount", "date", "text"] as const;
export type ColumnKind = (typeof COLUMN_KINDS)[number];
export type PasteRow = {
  /** 1-based row of the paste. */
  row: number;
  cells: string[];
  item: PasteItem | null;
  link: PasteLink | null;
  /** What found it, and the text that did. */
  by: "sku" | "vendor_sku" | "barcode" | null;
  key: string;
  qty: number | null;
  /** null: the vendor's price is used. */
  price: number | null;
  date: string | null;
  /** Why it can't be added (no item, no qty, a price it can't read…); empty = it can. */
  problems: string[];
  /** It can be added, but look (MOQ, multiple, a price far from the vendor's, on the PO already…). */
  warnings: string[];
  qtyWarnings: QtyWarning[];
};
export type PasteResult = {
  columns: ColumnKind[];
  /** The header row, when the paste had one (it isn't a line). */
  header: string[] | null;
  /** No header named the columns: they were worked out from what they hold (check them). */
  guessed: boolean;
  /** Two columns of one kind ("D and F are both Qty"): nothing can be added until one is set to Ignore. */
  conflict: string;
  rows: PasteRow[];
  /** Rows that can be added. */
  ok: PasteRow[];
};
/** The most lines one paste adds (po.add_lines takes 500). */
export const MAX_PASTE_LINES = 500;

// Reading cells ----------------------------------------------------------------------------------------------

/** Splits a paste into rows of cells: tabs (a spreadsheet), else CSV (commas or semicolons, quotes), else 2+ spaces. */
export function splitPaste(text: string): { rows: string[][]; lines: string[]; by: "tab" | "csv" | "spaces" } {
  const lines = text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((l) => l.trim());
  if (!lines.length) return { rows: [], lines, by: "tab" };
  if (lines.some((l) => l.includes("\t"))) return { rows: lines.map((l) => l.split("\t").map((c) => c.trim())), lines, by: "tab" };
  // A comma inside a number (1,200) doesn't make it CSV.
  const bare = (l: string) => l.replace(/(\d),(\d{3})(?!\d)/g, "$1$2");
  const sep = [",", ";"].find((s) => lines.filter((l) => bare(l).includes(s)).length >= Math.ceil(lines.length / 2));
  if (sep) return { rows: lines.map((l) => csvCells(l, sep)), lines, by: "csv" };
  return {
    lines,
    rows: lines.map((l) =>
      l
        .trim()
        .split(/\s{2,}|\s*\|\s*/)
        .map((c) => c.trim()),
    ),
    by: "spaces",
  };
}

function csvCells(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"' && !cur.trim()) quoted = true;
    else if (ch === sep) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

const CURRENCIES: [RegExp, string][] = [
  [/hk\$|hkd/i, "HKD"],
  [/us\$|usd|\$/i, "USD"],
  [/rmb|cny|¥|￥|元/i, "CNY"],
  [/€|eur/i, "EUR"],
  [/£|gbp/i, "GBP"],
];

/**
 * A number in a cell and the currency it's marked with: "1,200", "1200 pcs", "$0.45", "US $0.42/pc", "0.42$", "¥3.5",
 * "RMB¥3.5", "€0.42", "0,45" (a decimal comma), "1.200,50", "(0.42)" and "−0.42" (negative). null when it isn't one.
 */
export function parseNumber(text: string): { value: number | null; currency: string | null } {
  let t = text.trim();
  const currency = CURRENCIES.find(([re]) => re.test(t))?.[1] ?? null;
  t = t
    .replace(/[−‒–]/g, "-")
    .replace(/(hk\$|hkd|us\s*\$|usd|rmb|cny|eur|gbp|[$¥￥€£元])/gi, "")
    .replace(/\s*(\/|per\s*)?(pcs|pc|units|unit|ea|each|sets|set|个|件)\.?$/i, "")
    .replace(/\s/g, "");
  let sign = 1;
  if (/^\(.*\)$/.test(t)) [t, sign] = [t.slice(1, -1), -1];
  if (t.startsWith("-")) [t, sign] = [t.slice(1), -sign];
  if (/^[1-9]\d{0,2}(,\d{3})+(\.\d+)?$/.test(t)) t = t.replace(/,/g, "");
  else if (/^[1-9]\d{0,2}(\.\d{3})+,\d+$/.test(t)) t = t.replace(/\./g, "").replace(",", ".");
  else if (/^\d+,\d{1,4}$/.test(t)) t = t.replace(",", ".");
  if (!/^\d*\.?\d+$/.test(t)) return { value: null, currency };
  const n = Number(t) * sign;
  return { value: Number.isFinite(n) ? n : null, currency };
}
export const parseAmount = (text: string) => parseNumber(text).value;

const MONTHS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
const monthOf = (s: string) => {
  const k = s.replace(/\.$/, "");
  const n = MONTHS.findIndex((m) => m === k || m.slice(0, 3) === k || (m === "september" && k === "sept"));
  return n + 1;
};
const iso = (y: number, m: number, d: number) => {
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return date.toISOString().slice(0, 10);
};
const dayNo = (isoDay: string) => Date.parse(`${isoDay}T00:00:00Z`) / 86_400_000;

/**
 * A date in a cell, as YYYY-MM-DD: 2026-12-31, 2026/12/31, 2026.12.31, 12/31/2026 (US month first; 31/12/2026 when the
 * first number can't be a month), 31.12.2026 (dots: day first), Dec 31 2026, 31 Dec 2026, Dec 31 (the year that puts it
 * nearest today: late orders have dates in the past). null when it isn't one.
 */
export function parseDate(text: string, today: string): string | null {
  const t = text.trim().toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
  let m = t.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) return iso(+m[1], +m[2], +m[3]);
  m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) return iso(+m[3], +m[2], +m[1]);
  m = t.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  if (m) return +m[1] > 12 ? iso(+m[3], +m[2], +m[1]) : iso(+m[3], +m[1], +m[2]);
  let mo = 0;
  let d = 0;
  let y: number | null = null;
  m = t.match(/^([a-z]{3,9}\.?) (\d{1,2})(?: (\d{4}))?$/);
  if (m && monthOf(m[1])) [mo, d, y] = [monthOf(m[1]), +m[2], m[3] ? +m[3] : null];
  else {
    m = t.match(/^(\d{1,2})[ -]([a-z]{3,9}\.?)(?:[ -](\d{4}))?$/);
    if (!m || !monthOf(m[2])) return null;
    [mo, d, y] = [monthOf(m[2]), +m[1], m[3] ? +m[3] : null];
  }
  if (y !== null) return iso(y, mo, d);
  const thisYear = Number(today.slice(0, 4));
  const options = [thisYear - 1, thisYear, thisYear + 1].map((yr) => iso(yr, mo, d)).filter((x): x is string => !!x);
  return options.sort((a, b) => Math.abs(dayNo(a) - dayNo(today)) - Math.abs(dayNo(b) - dayNo(today)))[0] ?? null;
}

/** 05/06/2026: May 6 here (US), June 5 elsewhere. */
const ambiguousDate = (text: string) => {
  const m = text.trim().match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
  return !!m && +m[1] <= 12 && +m[2] <= 12 && m[1] !== m[2];
};

// Columns ------------------------------------------------------------------------------------------------------

/** Header words for a quantity still to come (what an open order needs), which wins over the ordered one. */
const OPEN_QTY = /open|remaining|outstanding|balance|backorder|due|未交/;

/** What a header cell names ("Qty (pcs)" → qty, "Price/pc" → price, "Pcs/ctn", "Order date", "Retail price" → nothing). */
export function headerKind(cell: string): ColumnKind | null {
  const k = cell.toLowerCase().replace(/[^a-z0-9一-鿿]/g, "");
  // Per carton / bag / box counts, weights, and what's been received or shipped already aren't the line's quantity.
  if (!k || /ctn|carton|box|pack|bag|weight|kg|cbm|gw|nw|received|shipped|delivered|sent|done/.test(k)) return null;
  if (
    /^(qty|quantity|orderqty|orderquantity|openqty|totalqty|totalquantity|totalpcs|pcs|units|数量)/.test(k) ||
    (OPEN_QTY.test(k) && /qty|quantity|pcs|units/.test(k))
  )
    return "qty";
  if (/retail|msrp|list|sale|sell|old|previous/.test(k)) return null;
  if ((/price|^up$|^uprice|unitcost|^cost|单价/.test(k) && !/total|amount/.test(k)) || k === "fob") return "price";
  if (/^(amount|total|subtotal|linetotal|value|金额|总价|总额)/.test(k)) return "amount";
  if (/^(order|po|invoice|pi|created|issue)/.test(k)) return null; // the order's own date, not when it's promised
  if (/date|^eta|promised|delivery|交期|交货/.test(k)) return "date";
  if (
    /^(sku|hhlsku|item|itemno|itemcode|code|product|productcode|vendorsku|theirsku|suppliersku|factorysku|partno|partnumber|pn|mpn|model|modelno|barcode|upc|ean|gtin|货号|型号|编号)$/.test(
      k,
    )
  )
    return "id";
  return null;
}

type Hit = { item: PasteItem; link: PasteLink | null; by: NonNullable<PasteRow["by"]> };
type Index = { find: (cell: string) => Hit | { ambiguous: string } | null };

/**
 * Items by HHL's SKU, this vendor's SKU and barcode (no case; barcodes without their leading zeros, which spreadsheets
 * drop). A key that names two different items is asked about, never picked; a vendor's linked item wins a SKU shared.
 */
function indexItems(items: readonly PasteItem[], links: readonly PasteLink[]): Index {
  const linkOf = new Map(links.map((l) => [l.item_id, l]));
  const live = items.filter((i) => !i.archived);
  const norm = (k: string, barcode = false) => (barcode ? k.trim().replace(/^0+/, "") : k.trim().toLowerCase());
  const add = (m: Map<string, PasteItem[]>, k: string, i: PasteItem, barcode = false) => {
    const key = norm(k, barcode);
    if (key) m.set(key, [...(m.get(key) ?? []), i]);
  };
  const bySku = new Map<string, PasteItem[]>();
  const byBarcode = new Map<string, PasteItem[]>();
  for (const i of live) {
    add(bySku, i.sku, i);
    if (/^\d{6,}$/.test(i.barcode.trim())) add(byBarcode, i.barcode, i, true);
  }
  // Part SKUs --- a sheet naming a part without its type prefix (GCU2-RM2 for MAT-GCU2-RM2) still finds it
  for (const i of live) if (hasSkuPrefix(i.sku) && !bySku.has(norm(skuStem(i.sku)))) add(bySku, skuStem(i.sku), i);
  const byId = new Map(live.map((i) => [i.id, i]));
  const byVendorSku = new Map<string, PasteItem[]>();
  for (const l of links) {
    const i = byId.get(l.item_id);
    if (i) add(byVendorSku, l.vendor_sku, i);
  }
  const pick = (list: PasteItem[] | undefined): PasteItem | "ambiguous" | null => {
    if (!list?.length) return null;
    const unique = [...new Set(list)];
    if (unique.length === 1) return unique[0];
    const linked = unique.filter((i) => linkOf.has(i.id));
    return linked.length === 1 ? linked[0] : "ambiguous";
  };
  const name = (i: PasteItem) => i.sku || i.name;
  return {
    find: (cell) => {
      const text = cell.trim();
      if (!text) return null;
      const hits: Hit[] = [];
      for (const [by, m, key] of [
        ["sku", bySku, norm(text)],
        ["vendor_sku", byVendorSku, norm(text)],
        ["barcode", byBarcode, /^\d{6,}$/.test(text) ? norm(text, true) : ""],
      ] as const) {
        const hit = key ? pick(m.get(key)) : null;
        if (hit === "ambiguous")
          return { ambiguous: `"${text}" is the ${by === "barcode" ? "barcode" : by === "sku" ? "SKU" : "vendor SKU"} of more than one item` };
        if (hit) hits.push({ item: hit, link: linkOf.get(hit.id) ?? null, by });
      }
      if (!hits.length) return null;
      const other = hits.find((h) => h.item.id !== hits[0].item.id);
      if (other)
        return {
          ambiguous: `"${text}" is ${hits[0].by === "sku" ? "HHL's SKU" : "a SKU"} of ${name(hits[0].item)} and ${other.by === "vendor_sku" ? "the vendor's SKU" : "the barcode"} of ${name(other.item)}`,
        };
      return hits[0];
    },
  };
}

const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  return s.length ? s[Math.floor(s.length / 2)] : 0;
};

/** What each column holds, from a header row if there is one, else from the cells. */
function guessColumns(rows: string[][], index: Index, today: string): { columns: ColumnKind[]; header: string[] | null } {
  const width = Math.max(...rows.map((r) => r.length));
  const first = rows[0] ?? [];
  const named = first.map(headerKind);
  const isItem = (c: string) => {
    const f = index.find(c);
    return !!f && !("ambiguous" in f);
  };
  if (named.filter(Boolean).length >= 2 && !first.some(isItem) && !first.some((c, k) => named[k] === "qty" && parseAmount(c) !== null)) {
    const columns = Array.from({ length: width }, (_, k) => named[k] ?? "text");
    // Ordered and open quantities side by side: an open order needs what's still to come.
    const qtys = columns.flatMap((c, k) => (c === "qty" ? [k] : []));
    const open = qtys.filter((k) => OPEN_QTY.test((first[k] ?? "").toLowerCase()));
    if (qtys.length > 1 && open.length === 1) for (const k of qtys) if (k !== open[0]) columns[k] = "text";
    return { columns, header: first };
  }
  const columns: ColumnKind[] = Array(width).fill("text");
  const cellsOf = (k: number) => rows.map((r) => r[k] ?? "").filter((c) => c.trim());
  const share = (k: number, test: (c: string) => boolean) => {
    const cells = cellsOf(k);
    return cells.length ? cells.filter(test).length / cells.length : 0;
  };
  // The identifier: the column most of whose cells are items.
  let idCol = -1;
  let best = 0;
  for (let k = 0; k < width; k++) {
    const s = share(k, (c) => !!index.find(c));
    if (s > best) [idCol, best] = [k, s];
  }
  if (idCol >= 0) columns[idCol] = "id";
  for (let k = 0; k < width; k++) if (k !== idCol && share(k, (c) => parseDate(c, today) !== null) >= 0.8) columns[k] = "date";
  // Numbers, but not codes (barcodes, factory numbers: 8+ digits) or row numbers (1, 2, 3…).
  const values = (k: number) => rows.map((r) => parseAmount(r[k] ?? ""));
  const numeric = Array.from({ length: width }, (_, k) => k).filter((k) => {
    if (columns[k] !== "text" || share(k, (c) => parseAmount(c) !== null) < 0.8) return false;
    if (share(k, (c) => /^\d{8,}$/.test(c.trim())) >= 0.5) return false;
    const v = values(k);
    return !(rows.length > 1 && v.every((x, n) => x === n + 1 || x === n));
  });
  const whole = (k: number) => values(k).every((x) => x === null || Number.isInteger(x));
  // Written like a price ("25.00", "0.42") or like a count ("500", "1,200").
  const decimals = (k: number) => share(k, (c) => /\d[.,]\d{1,4}\b/.test(c) && !/^\d{1,3}(,\d{3})+$/.test(c.trim())) >= 0.5;
  const med = (k: number) => median(values(k).filter((x): x is number => x !== null));
  /** How much a (qty, price) pair looks right: a count and a price as written, then the quantity the bigger. */
  const fit = (q: number, p: number) => (decimals(q) ? 0 : 2) + (decimals(p) ? 1 : 0);
  // qty × price = amount on most rows names all three; the amount is the rightmost such column (a PI's cartons × pieces
  // per carton = pieces comes before it), and the quantity the bigger of the two.
  let trio: [number, number, number] | null = null;
  for (const a of [...numeric].reverse()) {
    for (const q of numeric)
      for (const p of numeric) {
        if (q === p || p === a || q === a || !whole(q)) continue;
        const fits = rows.filter((r) => {
          const [x, y, z] = [parseAmount(r[q] ?? ""), parseAmount(r[p] ?? ""), parseAmount(r[a] ?? "")];
          return x !== null && y !== null && z !== null && x > 0 && Math.abs(x * y - z) <= Math.max(0.011, Math.abs(z) * 0.001);
        }).length;
        if (fits < Math.max(1, Math.ceil(rows.length * 0.6))) continue;
        const better = !trio || fit(q, p) > fit(trio[0], trio[1]) || (fit(q, p) === fit(trio[0], trio[1]) && med(q) > med(trio[0]));
        if (better) trio = [q, p, a];
      }
    if (trio) break;
  }
  if (trio) [columns[trio[0]], columns[trio[1]], columns[trio[2]]] = ["qty", "price", "amount"];
  else {
    // A whole-number column for the quantity (the bigger one, after the item), the other for the price.
    const after = numeric.filter((k) => k > idCol);
    const pool = after.length ? after : numeric;
    const q = pool.filter(whole).sort((x, y) => Number(decimals(x)) - Number(decimals(y)) || med(y) - med(x))[0];
    if (q !== undefined) {
      columns[q] = "qty";
      const p = pool.filter((k) => k !== q).sort((x, y) => Number(decimals(y)) - Number(decimals(x)))[0];
      if (p !== undefined) columns[p] = "price";
    }
  }
  return { columns, header: null };
}

// Rows ---------------------------------------------------------------------------------------------------------

const money = (n: number, currency: string) => `${currency === "USD" ? "$" : `${currency} `}${n < 1 ? String(Number(n.toFixed(4))) : n.toFixed(2)}`;

/**
 * A paste read into lines for one vendor's PO. `links` are this vendor's links; `onPo` the items already on the PO (item
 * id → its line number), so the preview can say a row adds a second line; `columns` what people set each column to.
 */
export function readPaste(
  text: string,
  data: {
    items: readonly PasteItem[];
    links: readonly PasteLink[];
    onPo?: ReadonlyMap<string, number>;
    today: string;
    /** The PO's currency: a price marked with another one is refused. */
    currency?: string;
    columns?: readonly ColumnKind[];
  },
): PasteResult {
  const { rows: cells, lines, by: splitBy } = splitPaste(text);
  const index = indexItems(data.items, data.links);
  if (!cells.length) return { columns: [], header: null, guessed: false, conflict: "", rows: [], ok: [] };
  const guess = guessColumns(cells, index, data.today);
  const header = guess.header;
  const columns = data.columns?.length === guess.columns.length ? [...data.columns] : guess.columns;
  const body = header ? cells.slice(1) : cells;
  const bodyLines = header ? lines.slice(1) : lines;
  // One column per kind (items may be found in several): two quantity columns are asked about, never picked between.
  const letter = (k: number) => String.fromCharCode(65 + (k % 26));
  const conflict = (["qty", "price", "amount", "date"] as const)
    .map((kind) => {
      const ks = columns.flatMap((c, k) => (c === kind ? [k] : []));
      return ks.length > 1
        ? `Columns ${ks.map(letter).join(" and ")} are both ${kind === "qty" ? "Qty" : kind === "price" ? "Unit price" : kind === "amount" ? "Amount" : "Date"}: set the one that isn't to Ignore`
        : "";
    })
    .filter(Boolean)
    .join(". ");
  const idCols = columns.flatMap((c, k) => (c === "id" ? [k] : []));
  const col = (kind: ColumnKind) => columns.indexOf(kind);
  // The usual number of cells: a row from a PDF with a blank cell has fewer, and its values sit in the wrong columns.
  const counts = new Map<number, number>();
  for (const r of body) counts.set(r.length, (counts.get(r.length) ?? 0) + 1);
  const usual = [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 0;
  const currency = data.currency ?? "USD";
  const seen = new Map<string, number>();
  const rows = body.map((r, n): PasteRow => {
    const row = n + (header ? 2 : 1);
    const problems: string[] = [];
    const warnings: string[] = [];
    if (splitBy !== "tab" && r.length !== usual)
      problems.push(
        `This row has ${r.length} cells and most have ${usual}: its values may be in the wrong columns (copy from the spreadsheet, or fill the blank)`,
      );
    // CSV with a number like 1,200 not in quotes: it was split in two.
    if (splitBy === "csv" && /(^|[^"\d])\d{1,3},\d{3}(?!\d)/.test(bodyLines[n] ?? ""))
      problems.push("A number like 1,200 without quotes was split in two: copy from the spreadsheet (tabs) instead");
    // The item: the item columns; with none, a cell that's exactly a SKU, vendor SKU or barcode (never a plain number).
    let hit: ReturnType<Index["find"]> = null;
    let key = "";
    const tries = idCols.length ? idCols : r.map((_, k) => k).filter((k) => columns[k] === "text" && !/^\d{1,7}$/.test((r[k] ?? "").trim()));
    for (const k of tries) {
      const found = index.find(r[k] ?? "");
      if (found) {
        [hit, key] = [found, (r[k] ?? "").trim()];
        if (!("ambiguous" in found)) break;
      }
    }
    const item = hit && !("ambiguous" in hit) ? hit.item : null;
    const link = hit && !("ambiguous" in hit) ? hit.link : null;
    if (hit && "ambiguous" in hit) problems.push(`${hit.ambiguous}: use the other code`);
    else if (!item)
      problems.push(
        idCols.length ? `No item has the SKU, vendor SKU or barcode ${idCols.map((k) => `"${r[k] ?? ""}"`).join(" / ")}` : "No item found in this row",
      );
    // Quantity.
    const qtyText = col("qty") >= 0 ? (r[col("qty")] ?? "") : "";
    const qty = qtyText ? parseAmount(qtyText) : null;
    if (col("qty") < 0) problems.push("No quantity column: set one above");
    else if (qty === null || qty <= 0) problems.push(`No quantity ("${qtyText}")`);
    else if (qty > 1_000_000) problems.push(`Qty ${qtyText} looks like a code, not a quantity`);
    else if (!Number.isInteger(qty)) warnings.push(`Qty ${qtyText} isn't a whole number (it's read as ${qty})`);
    else if (/^\d{1,3}\.\d{3}$/.test(qtyText.trim()))
      warnings.push(`Qty ${qtyText} is read as ${qty}: if it means ${qtyText.replace(".", ",")}, write ${qtyText.replace(".", "")}`);
    // Price: pasted (or the amount ÷ qty), else the vendor's; one it can't read, or in another currency, is refused.
    let price: number | null = null;
    const priceText = col("price") >= 0 ? (r[col("price")] ?? "").trim() : "";
    const amountText = col("amount") >= 0 ? (r[col("amount")] ?? "").trim() : "";
    const priced = priceText ? parseNumber(priceText) : amountText ? parseNumber(amountText) : null;
    if (priced) {
      const shown = priceText || amountText;
      if (priced.value === null) problems.push(`Can't read the ${priceText ? "price" : "amount"} "${shown}": fix it, or empty it to use the vendor's price`);
      else if (priced.value < 0) problems.push(`A price below 0 ("${shown}")`);
      else if (priced.currency && priced.currency !== currency) problems.push(`"${shown}" is in ${priced.currency}, but this PO is in ${currency}`);
      else price = priceText ? priced.value : qty ? Math.round(((priced.value as number) / qty) * 10_000) / 10_000 : null;
    }
    if (price === 0) warnings.push("A price of 0: free?");
    // A price, a quantity and an amount that don't agree: something is in the wrong column.
    const amount = amountText && priceText ? parseAmount(amountText) : null;
    if (price !== null && qty && amount !== null && Math.abs(qty * price - amount) > Math.max(0.011, Math.abs(amount) * 0.001))
      problems.push(`${qty} × ${money(price, currency)} isn't the amount ${amountText}: check the columns`);
    const listed = link && qty ? link.unit_price : null;
    if (price && listed && (price > listed * 3 || price < listed / 3))
      warnings.push(
        `${money(price, currency)} is ${price > listed ? `${Math.round((price / listed) * 10) / 10}×` : `1/${Math.round((listed / price) * 10) / 10} of`} the vendor's ${money(Number(listed), currency)}: the right column?`,
      );
    // Promised date.
    const dateText = col("date") >= 0 ? (r[col("date")] ?? "").trim() : "";
    const date = dateText ? parseDate(dateText, data.today) : null;
    if (dateText && !date && !/^(tbd|tba|-|n\/a)$/i.test(dateText)) warnings.push(`"${dateText}" isn't a date we can read: left blank`);
    else if (date && ambiguousDate(dateText)) warnings.push(`"${dateText}" read as ${date} (month first): write ${date} if that's wrong`);
    else if (date && date < data.today && !/\d{4}/.test(dateText))
      warnings.push(`"${dateText}" read as ${date}, in the past: add the year if it's next year's`);
    const qtyWarns = item && link && qty ? qtyWarnings(link, qty) : [];
    for (const w of qtyWarns) warnings.push(w.message);
    if (item && !link) warnings.push("Not linked to this vendor: no vendor SKU, and the price comes from the paste only");
    if (item && data.onPo?.has(item.id)) warnings.push(`Already on this PO (line ${data.onPo.get(item.id)}): its quantity is added to that line`);
    if (item) {
      const before = seen.get(item.id);
      if (before) warnings.push(`On the paste twice (row ${before} too): one line for both`);
      else seen.set(item.id, row);
    }
    return { row, cells: r, item, link, by: hit && !("ambiguous" in hit) ? hit.by : null, key, qty, price, date, problems, warnings, qtyWarnings: qtyWarns };
  });
  return { columns, header, guessed: !header, conflict, rows, ok: conflict ? [] : rows.filter((r) => !r.problems.length) };
}
